import { generatePath } from 'react-router-dom';
import { Params } from 'react-router';

interface IOptions {
    search?: Record<string, string> | string | undefined;
    excludeSearch?: string[];
    keepSearch?: boolean;
}

export const safeInternalUrl = (value: string | null | undefined): string => {
    try {
        const destination = new URL(value || '/', window.location.origin);
        const isInternal =
            destination.origin === window.location.origin &&
            destination.protocol === window.location.protocol &&
            // `https://app//evil.test` is same-origin but its path would be a protocol-relative URL.
            !destination.pathname.startsWith('//');
        if (isInternal) return destination.pathname + destination.search + destination.hash;
    } catch {
        // Malformed return destinations fall back to the app home page.
    }
    return '/';
};

// OAuth destinations come from the backend's provider configuration. Callers with
// a fixed provider also check its origin; never use this for a user-supplied `next`.
export const parseRedirectUrl = (value: string): URL => {
    if (!value?.trim()) throw new Error('Missing redirect URL');
    const destination = new URL(value, window.location.origin);
    const isHttps = destination.protocol === 'https:';
    // Plain http is only acceptable for the app itself (local development).
    const isSameOriginHttp = destination.protocol === 'http:' && destination.origin === window.location.origin;
    if (!(isHttps || isSameOriginHttp) || destination.username || destination.password) {
        throw new Error('Invalid redirect URL');
    }
    return destination;
};

export const redirectTo = (value: string) => {
    window.location.href = parseRedirectUrl(value).href;
};

export const url = (path: string, params?: Params, options: IOptions = { search: undefined }) => {
    /*
    Replace variables in paths like:
    url('/orders/:recordId', { recordId: 123 }) -> '/orders/123'
    */
    const compiledPath = generatePath(path, params);

    const searchParams = new URLSearchParams(options.search);
    if (options.keepSearch) {
        const currentSearchParams = new URLSearchParams(window.location.search);
        currentSearchParams.forEach((value, key) => {
            if (options.excludeSearch?.includes(key)) {
                return;
            }

            searchParams.set(key, value);
        });
    }

    const queryString = searchParams.toString();
    return queryString ? `${compiledPath}?${queryString}` : compiledPath;
};
